<template>
  <v-dialog
    v-model="show"
    max-width="900px"
  >
    <v-card>
      <v-card-title>
        Request For Benefit
        <v-spacer></v-spacer>
        <v-btn
          icon
          @click="show = false"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="py-2">
        <v-container class="py-0">
          <v-row>
            <v-col cols="12" lg="3" md="3">
              <h4 class="mb-1">Available Amount</h4>
              <p
                v-if="typeof availableCompensationAmount === 'number'"
                class="mb-0"
              >
                <b>{{ currencies.USD.sign }}{{ availableCompensationAmount }}</b>
              </p>
              <p class="mb-0" v-else>Loading...</p>
            </v-col>
            <v-col cols="12" lg="3" md="3">
              <h4 class="mb-2">Used Amount</h4>
              <p
                v-if="typeof availableCompensationAmount === 'number'"
                class="mb-0"
              >
                <b>{{ currencies.USD.sign }}{{ usedCompensationSum }}</b>
              </p>
              <p class="mb-0" v-else>Loading...</p>
            </v-col>
            <v-col cols="12" lg="3" md="3">
              <h4 class="mb-2">Amount In Progress</h4>
              <p
                v-if="typeof availableCompensationAmount === 'number'"
                class="mb-0"
              >
                <b>{{ currencies.USD.sign }}{{ amountInProgress }}</b>
              </p>
              <p class="mb-0" v-else>Loading...</p>
            </v-col>
            <v-col cols="12" lg="3" md="3">
              <h4 class="mb-2">Current {{ currencies.USD.name }} rate</h4>
              <p
                v-if="NBUExchangeRate"
                class="mb-0"
              >
                <b>{{ NBUExchangeRate }}{{ currencies.UAH.sign }}</b>
              </p>
              <p class="mb-0" v-else>Loading...</p>
            </v-col>
          </v-row>
          <div v-if="expenditureType && expenditureType.Limit">
            <v-divider class="mt-3"></v-divider>
            <h3 class="mt-4">{{ expenditureType.Name }}</h3>
            <v-row class="mt-0 mb-1">
              <v-col class="pt-1" cols="12" lg="3" md="3">
                <h4 class="mb-1">Available Amount</h4>
                <p
                  v-if="typeof expenditureType.Limit === 'number' && typeof availableAmountInCompCategory === 'number'"
                  class="mb-0"
                >
                  <b>{{ currencies.USD.sign }}{{ availableCompensationAmount > availableAmountInCompCategory ? availableAmountInCompCategory : availableCompensationAmount }}</b>
                </p>
                <p class="mb-0" v-else>Loading...</p>
              </v-col>
              <v-col class="pt-1" cols="12" lg="3" md="3">
                <h4 class="mb-2">Used Amount</h4>
                <p
                  v-if="typeof expenditureType.Limit === 'number' && typeof expenditureType.Used_Amount === 'number'"
                  class="mb-0"
                >
                  <b>{{ currencies.USD.sign }}{{ expenditureType.Used_Amount }}</b>
                </p>
                <p class="mb-0" v-else>Loading...</p>
              </v-col>
              <v-col class="pt-1" cols="12" lg="3" md="3">
                <h4 class="mb-2">Amount In Progress</h4>
                <p
                  v-if="typeof expenditureType.Limit === 'number' && typeof expenditureType.Amount_In_Progress === 'number'"
                  class="mb-0"
                >
                  <b>{{ currencies.USD.sign }}{{ expenditureType.Amount_In_Progress }}</b>
                </p>
                <p class="mb-0" v-else>Loading...</p>
              </v-col>
              <v-col class="pt-1" cols="12" lg="3" md="3">
                <h4 class="mb-1">Compensation Limit</h4>
                <p
                  v-if="typeof expenditureType.Limit === 'number'"
                  class="mb-0"
                >
                  <b>{{ currencies.USD.sign }}{{ expenditureType.Limit }}</b>
                </p>
                <p class="mb-0" v-else>Loading...</p>
              </v-col>
            </v-row>
          </div>
        </v-container>
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header class="ml-0">Attached files</v-expansion-panel-header>
            <v-expansion-panel-content>
              Please upload the documents: <br/>
              1. A payment receipt/check confirming the transaction. <br/>
              2. A document containing the employee's name, date, amount, and information about the seller, including
              the authorized person's company name, seal, and signature.
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-row class="mb-2">
          <v-col cols="12" lg="6" md="6">
            <v-file-input
              label="Payment check"
              show-size
              counter
              @change="onPaymentCheckChange"
              :accept="ALLOWED_FILE_TYPES"
              v-model="paymentCheckFile"
              :error-messages="sameFilesError"
              :rules="[rules.required]"
            >
            </v-file-input>
          </v-col>
          <v-col cols="12" lg="6" md="6">
            <v-file-input
              label="Agreement"
              show-size
              @change="onAgreementDocumentChange"
              counter
              v-model="agreementDocument"
              :error-messages="sameFilesError"
              :accept="ALLOWED_FILE_TYPES"
            >
            </v-file-input>
          </v-col>
        </v-row>
        <v-expansion-panels>
          <v-expansion-panel>
            <v-expansion-panel-header class="ml-0">To ensure accurate record-keeping, please follow these steps:
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              1. Select the currency used for the payment.<br/>
              2. If the payment was made in UAH, also specify the date of the payment.<br/>
              3. Select a compensation type. <br/>
              4. Enter the amount of money spent.
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
        <v-row>
          <v-col cols="12" md="2" lg="2">
            <v-select
              v-model="currency"
              :prepend-icon="currency === currencies.USD.name ? 'mdi-currency-usd' : 'mdi-currency-uah'"
              :items="currencyNames"
              @change="changeCurrency"
            >
            </v-select>
          </v-col>
          <v-col cols="12" md="3" lg="3">
            <v-text-field
              v-model="compensationInput"
              :disabled="isAvailableAmountUsed"
              label="Compensation amount"
              type="number"
              class="inputAmount"
              hint="Enter the full amount you paid"
              :rules="[rules.required, rules.availableAmount]"
            >
              <template v-slot:append v-if="compensationAmount">
                <span>{{ currencies.USD.sign }}{{ compensationAmount }}</span>
              </template>
            </v-text-field>
          </v-col>
          <v-col cols="12" md="3" lg="3">
            <v-select
              v-model="expenditureType"
              label="Compensation type"
              :items="expenditureTypes"
              item-text="Name"
              return-object
              @change="getAlreadyUsedSumByCompTypeId"
            >
            </v-select>
          </v-col>
          <v-col cols="12" md="4" lg="4" v-if="currency === currencies.UAH.name">
            <DatePicker
              v-model="paymentDate"
              label="Payment Date"
              :max="today"
              @onDatePickerChange="getNBUExchangeRate"
            />
          </v-col>
        </v-row>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions>
        <span
          class="orange--text text--darken-2"
          v-if="isAvailableAmountUsed"
        >
          {{ this.availableCompensationAmount > 0 ? `The maximum available amount of compensation for ${expenditureType.Name} has been used` : 'The maximum available compensation amount was used'}}
        </span>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="!isComponentDataReady || isRequestReady || isLoading"
          :loading="isLoading"
          small
          color="primary"
          @click="sendRequest"
        >
          Send
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
<script>
import { ALLOWED_FILE_TYPES } from '@/constants/fileAccept'
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'
export default {
  name: 'ExpenditureCompensationRequest',
  components: {
    DatePicker: () => import('@/components/global/DatePicker.vue')
  },
  props: {
    value: {
      required: false,
      default: false,
      type: Boolean
    },
    requestId: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      expenditureType: null,
      expenditureTypes: [],
      compensationInput: null,
      availableCompensationAmount: null,
      availableAmountInCompCategory: null,
      compensationLimitType: 'Benefit Compensation',
      maxCompensationAmount: null,
      today: formatLocalDate(),
      compensationAmount: null,
      paymentCheckFile: null,
      agreementDocument: null,
      NBUExchangeRate: null,
      isNewConfirmationFile: false,
      paymentCheckFileHash: null,
      agreementDocumentHash: null,
      areFilesIdentical: false,
      isCheckingFiles: false,
      fileCheckVersion: 0,
      paymentDate: formatLocalDate(),
      currency: 'USD',
      usedCompensationSum: null,
      amountInProgress: null,
      currencyNames: ['USD', 'UAH'],
      isLoading: false,
      ALLOWED_FILE_TYPES,
      currencies: {
        USD: {
          sign: '$',
          name:
            'USD'
        },
        UAH: {
          sign: '₴',
          name:
            'UAH'
        }
      },
      rules: {
        required: v => !!v || 'Required',
        availableAmount: (val) => this.checkOnAvailableAmount(val)
      }
    }
  },
  async mounted () {
    this.getCompensationTypes()
    await this.getNBUExchangeRate()
    await this.getCompensationLimitByType()
    await this.getAlreadyUsedCompensationSum()

    this.calculateAvailableCompensationAmount()

    if (this.requestId) {
      this.getExpenditureCompensationRequest(this.requestId)
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    },
    show: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
        this.$emit('getHistory')
      }
    },
    isRequestReady () {
      return !this.paymentCheckFile ||
        !this.compensationAmount ||
        !this.expenditureType?.Type_ID ||
        this.isCheckingFiles ||
        this.areFilesIdentical
    },
    sameFilesError () {
      return this.areFilesIdentical
        ? ['Payment check and Agreement must be different files']
        : []
    },
    isComponentDataReady () {
      return typeof this.maxCompensationAmount === 'number' && typeof this.NBUExchangeRate === 'number' &&
        typeof this.usedCompensationSum === 'number' && typeof this.amountInProgress === 'number'
    },
    isAvailableAmountUsed () {
      return (typeof this.availableCompensationAmount === 'number' && this.availableCompensationAmount <= 0) ||
      (typeof this.availableAmountInCompCategory === 'number' && this.availableAmountInCompCategory <= 0)
    }
  },
  methods: {
    async calculateFileSHA256 (file) {
      if (!file) return null

      const buffer = await file.arrayBuffer()
      const hashBuffer = await crypto.subtle.digest('SHA-256', buffer)
      const hashArray = Array.from(new Uint8Array(hashBuffer))

      return hashArray
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('')
    },
    async checkFilesEquality () {
      const checkVersion = ++this.fileCheckVersion

      this.areFilesIdentical = false
      this.paymentCheckFileHash = null
      this.agreementDocumentHash = null

      if (!this.paymentCheckFile || !this.agreementDocument) {
        this.isCheckingFiles = false
        return
      }

      this.isCheckingFiles = true

      try {
        const [paymentCheckFileHash, agreementDocumentHash] = await Promise.all([
          this.calculateFileSHA256(this.paymentCheckFile),
          this.calculateFileSHA256(this.agreementDocument)
        ])

        if (checkVersion !== this.fileCheckVersion) return

        this.paymentCheckFileHash = paymentCheckFileHash
        this.agreementDocumentHash = agreementDocumentHash
        this.areFilesIdentical = paymentCheckFileHash === agreementDocumentHash
      } catch (error) {
        if (checkVersion !== this.fileCheckVersion) return

        this.$store.dispatch('setError', {
          errorMsg: 'File SHA-256 calculation error: ' + error.message
        })
      } finally {
        if (checkVersion === this.fileCheckVersion) {
          this.isCheckingFiles = false
        }
      }
    },
    async onPaymentCheckChange () {
      this.isNewConfirmationFile = true
      await this.checkFilesEquality()
    },
    async onAgreementDocumentChange () {
      this.isNewConfirmationFile = true
      await this.checkFilesEquality()
    },
    convertAmountInUSDCeil (val) {
      if (this.currency === this.currencies.USD.name) {
        if (!Number.isInteger(val)) {
          return Math.ceil(val)
        } else {
          return val
        }
      } else if (this.currency === this.currencies.UAH.name) {
        return Math.ceil(val / this.NBUExchangeRate)
      }
    },
    checkOnAvailableAmount (val) {
      const valNumber = Number(val)
      const convertedAmount = this.convertAmountInUSDCeil(valNumber)
      this.compensationAmount = null
      if (valNumber <= 0) {
        return 'Cannot be less than or equal to 0'
      } else if (!Number.isInteger(valNumber) && val.split('.')[1].length > 2) {
        return 'More than two decimal places'
      }
      if (convertedAmount > this.availableCompensationAmount) {
        if (this.currency === this.currencies.USD.name) {
          return `Exceeds maximum available limit of ${this.currencies.USD.sign}${this.availableCompensationAmount}`
        } else {
          const maxAmountInUAH = this.NBUExchangeRate * this.availableCompensationAmount
          const truncatedAmount = Math.floor(maxAmountInUAH * 100) / 100
          return `Exceeds maximum available limit of ${truncatedAmount} ${this.currencies.UAH.sign}`
        }
      } else if (this.expenditureType && typeof this.expenditureType.Limit === 'number') {
        if (convertedAmount > this.availableAmountInCompCategory) {
          if (this.currency === this.currencies.USD.name) {
            return `Exceeds maximum compensation type limit of ${this.currencies.USD.sign}${this.availableAmountInCompCategory}`
          } else {
            const maxAmountInUAH = this.NBUExchangeRate * this.availableAmountInCompCategory
            const truncatedAmount = Math.floor(maxAmountInUAH * 100) / 100
            return `Exceeds maximum compensation type limit of ${truncatedAmount} ${this.currencies.UAH.sign}`
          }
        }
      }
      this.compensationAmount = convertedAmount
      return true
    },
    calculateAvailableCompensationAmount () {
      this.availableCompensationAmount = this.maxCompensationAmount - this.usedCompensationSum - this.amountInProgress
    },
    calculateAvailableAmountInCompCategory () {
      this.availableAmountInCompCategory = this.expenditureType.Limit - this.expenditureType.Used_Amount - this.expenditureType.Amount_In_Progress
    },
    changeCurrency () {
      this.compensationAmount = null
      this.compensationInput = null
    },
    async getAlreadyUsedCompensationSum () {
      await this.axios.get(`/api/requests/used_compensation_sum/${this.user.employee_id}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.usedCompensationSum = res.data[0].Used_Amount
        this.amountInProgress = res.data[0].Amount_In_Progress
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getAlreadyUsedSumByCompTypeId (e) {
      this.availableAmountInCompCategory = null

      if (e && typeof e.Limit === 'number') {
        this.axios.get(`/api/requests/used_compensation_sum_by_comp_type_id/${e.Type_ID}/${this.user.employee_id}`, {
          headers: {
            Authorization: 'Bearer ' + this.user.token
          }
        }).then(res => {
          this.expenditureType.Amount_In_Progress = res.data[0].Amount_In_Progress
          this.expenditureType.Used_Amount = res.data[0].Used_Amount
          this.calculateAvailableAmountInCompCategory()

          if (this.compensationInput !== null && this.compensationInput !== '') {
            this.checkOnAvailableAmount(this.compensationInput)
          }
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
      } else {
        if (this.compensationInput !== null && this.compensationInput !== '') {
          this.checkOnAvailableAmount(this.compensationInput)
        }
      }
    },
    getExpenditureCompensationRequest (requestId) {
      this.axios.get(`/api/requests/expenditure_compensation_by_id/${requestId}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const data = res.data[0]
        this.compensationInput = data.Amount
        this.compensationAmount = data.Amount
        this.expenditureType = {
          Type_ID: data.Expenditure_Compensation_Type_ID,
          Name: data.Expenditure_Compensation_Type
        }
        this.paymentCheckFile = this.bufferToFile(
          data.Payment_File?.data,
          data.Payment_File_Mimetype,
          data.Payment_File_Name
        )

        this.agreementDocument = this.bufferToFile(
          data.Agreement_File?.data,
          data.Agreement_File_Mimetype,
          data.Agreement_File_Name
        )

        this.checkFilesEquality()
      }).catch(err => {
        const errorMsg = getApiErrorMessage(err)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    sendRequest () {
      if (this.isCheckingFiles || this.areFilesIdentical) return

      this.isLoading = true
      const headers = { Authorization: 'Bearer ' + this.user.token }

      const data = {
        Payment_Document: this.paymentCheckFile,
        Agreement_Document: this.agreementDocument,
        Expenditure_Type_ID: this.expenditureType.Type_ID,
        Amount: this.compensationAmount,
        isNewConfirmationFile: this.isNewConfirmationFile
      }

      const formData = this.toFormData(
        this.requestId
          ? data
          : { ...data, Creator_ID: this.user.employee_id, Payout_Month: formatLocalDate() }
      )

      const request = this.requestId
        ? this.axios.put(`/api/requests/expenditure_compensation/${this.requestId}`, formData, { headers })
        : this.axios.post('/api/requests/expenditure_compensation', formData, { headers })

      request
        .then(() => {
          this.show = false
          this.$store.dispatch('setSuccess', {
            successMsg: this.requestId
              ? 'Request for expenditure compensation was updated'
              : 'Request for expenditure compensation was made'
          })
        })
        .catch(err => {
          const errorMsg = getApiErrorMessage(err)
          this.$store.dispatch('setError', { errorMsg })
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    toFormData (obj) {
      const formData = new FormData()
      Object.entries(obj).forEach(([key, value]) => {
        formData.append(key, value)
      })
      return formData
    },
    getCompensationTypes () {
      this.axios.get('/api/expenditure_compensation_types', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.expenditureTypes = res.data
      }).catch(err => {
        const errorMsg = getApiErrorMessage(err)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    async getCompensationLimitByType () {
      await this.axios.get(`/api/payroll/compensation_limit_by_type/${this.compensationLimitType}?employeeId=${this.user.employee_id}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        if (!res.data.length) {
          this.$store.dispatch('setError', { errorMsg: 'Error getting maximum limit' })
          return
        }
        if (res.data[0].Compensation_Currency === this.currencies.UAH.name) {
          this.maxCompensationAmount = Math.ceil(res.data[0].Compensation_Limit / this.NBUExchangeRate)
          return
        }
        this.maxCompensationAmount = res.data[0].Compensation_Limit
      }).catch(err => {
        const errorMsg = getApiErrorMessage(err)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    bufferToFile (bufferData, mimetype, filename) {
      if (!bufferData || !mimetype || !filename) return null
      const byteArray = new Uint8Array(bufferData)
      const blob = new Blob([byteArray], { type: mimetype })
      return new File([blob], filename, {
        type: mimetype,
        lastModified: Date.now()
      })
    },
    async getNBUExchangeRate () {
      this.compensationAmount = null
      this.compensationInput = null
      const paymentDate = this.paymentDate.replace(/-/g, '')
      await this.axios.get(`https://bank.gov.ua/NBUStatService/v1/statdirectory/exchange?valcode=USD&date=${paymentDate}&json`)
        .then(res => {
          if (res.status === 200 && res.data.length && res.data[0]?.rate && typeof res.data[0].rate === 'number') {
            this.NBUExchangeRate = res.data[0].rate
          } else {
            let errorMsg = 'NBU API Error: Invalid API response or data format'
            if (!res.data.length) errorMsg = 'NBU API Error: No data returned'
            if (res.data.length && !res.data[0]?.rate) errorMsg = 'NBU API Error: rate missing in response'
            if (res.data.length && res.data[0]?.rate && typeof res.data[0].rate !== 'number') errorMsg = 'NBU API Error: rate is not a number'

            this.$store.dispatch('setError', { errorMsg })
          }
        }).catch(error => {
          const errorMsg = getApiErrorMessage(error)
          this.$store.dispatch('setError', { errorMsg })
        })
    }
  }
}
</script>
<style lang="scss" scoped>
.inputAmount input[type='number'] {
  -moz-appearance: textfield;
}

.inputAmount input::-webkit-outer-spin-button,
.inputAmount input::-webkit-inner-spin-button {
  -webkit-appearance: none;
}
</style>
